import { type Group, type Organization, type Status } from "@entities";
import {
  GROUP_VOLUNTEER_REGION_LABELS,
  parseGroupVolunteerRegions,
} from "@shared/helpers/group-volunteer-regions";
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { groupOpportunityRepo, groupRepo } from "@repositories";
import { type ColumnProps, type HyperTableDef } from "@shared/frontend/hyper-table/types";
import dayjs from "dayjs";
import { formatOpportunitySchedule } from "@shared/helpers/format-opportunity-schedule";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import PhoneNumberCell from "../PhoneNumberCell";

export const useGroupColumns = (
  currOrganization: Organization | undefined,
  setAlertText: any,
  setSeverity: any,
  hyperTable: HyperTableDef<Group>,
  registeredCountById: Record<string, number> = {},
): ColumnProps<Group>[] => {
  const navigate = useNavigate();
  const [arrivedOverrides, setArrivedOverrides] = useState<
    Record<string, boolean>
  >({});
  const [arrivedPending, setArrivedPending] = useState<Record<string, boolean>>(
    {},
  );
  const arrivedReqSeq = useRef<Record<string, number>>({});

  const arrivedKey = (groupId: string, opportunityId: string) =>
    `${groupId}:${opportunityId}`;

  const renderOpportunityLine = (
    link: {
      groupId?: string;
      opportunityId?: string;
      createdAt?: Date | string | null;
      internalArrived?: boolean;
      opportunity?: {
        id: string;
        title: string;
        hasTime?: boolean;
        hasStartTime?: boolean;
        startDate?: Date | null;
        endDate?: Date | null;
        timeDescription?: string | null;
      };
    },
    groupId: string,
    key: string | number,
  ) => {
    const opportunity = link.opportunity;
    if (!opportunity) return null;
    const registeredAt = link.createdAt
      ? dayjs(link.createdAt).format("DD/MM/YYYY")
      : null;
    const opportunityId = link.opportunityId ?? opportunity.id;
    const overrideKey = arrivedKey(groupId, opportunityId);
    const arrived =
      overrideKey in arrivedOverrides
        ? arrivedOverrides[overrideKey]
        : !!link.internalArrived;
    const isPending = !!arrivedPending[overrideKey];

    const toggleArrived = async (checked: boolean) => {
      const prevValue =
        overrideKey in arrivedOverrides
          ? arrivedOverrides[overrideKey]
          : !!link.internalArrived;
      const seq = (arrivedReqSeq.current[overrideKey] ?? 0) + 1;
      arrivedReqSeq.current[overrideKey] = seq;
      setArrivedOverrides((prev) => ({ ...prev, [overrideKey]: checked }));
      setArrivedPending((prev) => ({ ...prev, [overrideKey]: true }));
      try {
        await groupOpportunityRepo.update(
          {
            groupId,
            opportunityId,
          },
          { internalArrived: checked },
        );
        if (arrivedReqSeq.current[overrideKey] !== seq) return;
        setAlertText(checked ? "סומן הגעה" : "בוטל סימון הגעה");
        setSeverity("success");
      } catch {
        if (arrivedReqSeq.current[overrideKey] !== seq) return;
        setArrivedOverrides((prev) => ({
          ...prev,
          [overrideKey]: prevValue,
        }));
        setAlertText("עדכון סימון הגעה נכשל");
        setSeverity("error");
      } finally {
        if (arrivedReqSeq.current[overrideKey] === seq) {
          setArrivedPending((prev) => {
            const next = { ...prev };
            delete next[overrideKey];
            return next;
          });
        }
      }
    };

    return (
      <Stack key={key} spacing={0.25} sx={{ mb: 0.75 }}>
        <div>
          <Typography
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/events/${opportunity.id}`);
            }}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
              "&:hover": { textDecoration: "none" },
            }}
          >
            {opportunity.title}
          </Typography>
          {` | ${formatOpportunitySchedule(opportunity)}`}
        </div>
        {registeredAt && (
          <Typography variant="caption" color="text.secondary">
            תאריך הרשמה להתנדבות: {registeredAt}
          </Typography>
        )}
        <FormControlLabel
          onClick={(e) => e.stopPropagation()}
          control={
            <Checkbox
              size="small"
              checked={arrived}
              disabled={isPending}
              onChange={(e) => void toggleArrived(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">הגיעה למשימה</Typography>
          }
          sx={{ m: 0 }}
        />
      </Stack>
    );
  };

  return [
    {
      field: "name",
      enableSorting: true,
      minSize: 250,
    },
    {
      field: "admin",
      minSize: 250,
      cell: ({ row }) => (
        <Stack alignItems="flex-start" flexDirection="column">
          <div>
            <strong>שם:</strong> {row.original.admin?.name}
          </div>
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
            <strong>מספר טלפון:</strong>
            <PhoneNumberCell phoneNumber={row.original.admin?.phoneNumber || ""} />
          </Stack>
          <div>
            <strong>מייל:</strong> {row.original.admin?.email}
          </div>
        </Stack>
      ),
    },
    { field: "size" },
    {
      field: "preferredVolunteerRegions",
      caption: "אזורי התנדבות",
      minSize: 140,
      cell: ({ row }) => {
        const regions = parseGroupVolunteerRegions(
          row.original.preferredVolunteerRegions,
        );
        return regions.length
          ? regions.map((r) => GROUP_VOLUNTEER_REGION_LABELS[r]).join(", ")
          : "-";
      },
    },
    {
      field: "associations",
      cell: ({ row }) => registeredCountById[row.original.id] ?? "-",
    },
    { field: "description" },
    {
      field: "status",
      cell: ({ row }) => {
        return (
          <Stack alignItems="center" flexDirection="row" sx={{ width: "100%", minWidth: 0 }}>
            <Select
              className="status-select-groups"
              labelId="select-label"
              value={row.original.status?.title || "ללא סטטוס"}
              sx={{
                backgroundColor: row.original.status?.color,
                color: row.original.status?.color ? "white" : "black",
                justifyContent: "center",
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                width: "100%",
                maxWidth: "100%",
                /* eslint-disable @typescript-eslint/naming-convention -- MUI sx */
                "& .MuiSelect-select": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
                /* eslint-enable @typescript-eslint/naming-convention -- MUI sx */
              }}
              IconComponent={undefined}
            >
              <MenuItem
                sx={{
                  backgroundColor: "white",
                  ["&:hover"]: {
                    backgroundColor: "#f0f0f0",
                  },
                  color: "black",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
                value="ללא סטטוס"
                onClick={async () => {
                  await groupRepo.update(row.original.id, {
                    statusId: null as any,
                  });
                  await hyperTable.context.current?.refetch("invalidate");
                  setAlertText("הסטטוס עודכן בהצלחה");
                  setSeverity("success");
                }}
              >
                ללא סטטוס
              </MenuItem>
              {/* Render other status options */}
              {currOrganization?.status
                ?.filter((status: Status) => status.type === "GROUP")
                .sort((a: Status, b: Status) => a.order - b.order)
                .map((status: Status) => (
                  <MenuItem
                    title={status.title}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      backgroundColor: `${status.color} !important`,
                      /* eslint-disable @typescript-eslint/naming-convention -- MUI sx*/
                      "&:hover": {
                        backgroundColor: `${status.color}CC`, // Keep the same color on hover
                      },
                      "&.Mui-selected": {
                        backgroundColor: `${status.color}CC`,
                        border: "3.5px solid white",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: `${status.color}CC`,
                      },
                      /* eslint-enable @typescript-eslint/naming-convention -- MUI sx*/
                      color: "white",
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center",
                    }}
                    key={status.id}
                    value={status.title}
                    onClick={async () => {
                      await groupRepo.update(row.original.id, {
                        statusId: status.id,
                      });
                      await hyperTable.context.current?.refetch("invalidate");
                      setAlertText("הסטטוס עודכן בהצלחה");
                      setSeverity("success");
                    }}
                  >
                    {status.title}
                  </MenuItem>
                ))}
            </Select>
          </Stack>
        );
      },
      preventRowClick: true,
      minSize: 200,
    },
    {
      field: "type",
    },
    {
      field: "preferredStartDate",
      cell: ({ row }) => {
        const { preferredStartDate } = row.original;
        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={
              heIL.components.MuiLocalizationProvider.defaultProps.localeText
            }
            adapterLocale="he"
          >
            <div
              className="flex gap-2"
              style={{ flexDirection: "row", display: "flex" }}
            >
              <DatePicker
                value={preferredStartDate ? dayjs(preferredStartDate) : null}
                onChange={async (start) => {
                  try {
                    await groupRepo.update(row.original.id, {
                      preferredStartDate: start
                        ? dayjs(start).startOf("day").toDate()
                        : undefined,
                    });
                    setAlertText("תאריך התחלה עודכן בהצלחה");
                    setSeverity("success");
                  } catch (e) {
                    setAlertText("שגיאה בעדכון תאריך התחלה");
                    setSeverity("error");
                  }
                }}
                reduceAnimations
                className="group-preferred-date-picker"
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    placeholder: "תאריך התחלה", // 👈 set your placeholder here
                    label: "", // 👈 hide the label
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        );
      },
      minSize: 200,
      preventRowClick: true,
    },
    {
      field: "preferredEndDate",
      cell: ({ row }) => {
        const { preferredEndDate } = row.original;
        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={
              heIL.components.MuiLocalizationProvider.defaultProps.localeText
            }
            adapterLocale="he"
          >
            <div
              className="flex gap-2"
              style={{ flexDirection: "row", display: "flex" }}
            >
              <DatePicker
                value={preferredEndDate ? dayjs(preferredEndDate) : null}
                onChange={async (end) => {
                  try {
                    await groupRepo.update(row.original.id, {
                      preferredEndDate: end
                        ? dayjs(end).endOf("day").toDate()
                        : undefined,
                    });
                    setAlertText("תאריך סיום עודכן בהצלחה");
                    setSeverity("success");
                  } catch (e) {
                    setAlertText("שגיאה בעדכון תאריך סיום");
                    setSeverity("error");
                  }
                }}
                reduceAnimations
                className="group-preferred-date-picker"
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    placeholder: "תאריך סיום", // 👈 set your placeholder here
                    label: "", // 👈 hide the label
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        );
      },
      minSize: 200,
      preventRowClick: true,
    },
    {
      field: "opportunities",
      caption: "משימות (תאריך הרשמה)",
      cell: ({ row }) => {
        return (
          <Stack onClick={(e) => e.stopPropagation()}>
            {row.original.opportunities?.map((event, index) =>
              renderOpportunityLine(
                event,
                row.original.id,
                event.opportunityId || index,
              ),
            )}
          </Stack>
        );
      },
      minSize: 260,
      enableSorting: false,
      preventRowClick: true,
    },
    {
      field: "interestedOpportunities",
      cell: ({ row }) => {
        return (
          <Stack onClick={(e) => e.stopPropagation()}>
            {row.original.interestedOpportunities
              ?.filter((interest) => !interest.hasDeclinedSurvey)
              ?.map((interest, index) => {
                const opportunity = interest.opportunity;
                if (!opportunity) return null;
                return (
                  <div key={interest.id || index}>
                    <Typography
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/events/${opportunity.id}`);
                      }}
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        textDecoration: "underline",
                        "&:hover": { textDecoration: "none" },
                      }}
                    >
                      {opportunity.title}
                    </Typography>
                    {` | ${formatOpportunitySchedule(opportunity)}`}
                  </div>
                );
              })}
          </Stack>
        );
      },
      minSize: 200,
      enableSorting: false,
      preventRowClick: true,
    },
    { field: "groupCoordinator" },
    { field: "transportMode" },
    { field: "comments" },
    { field: "createdAt" },
  ];
};
